import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, TextField } from '@cfa/react-components';
import { Radio, RadioGroup } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import Constants from 'constants/index';
import { validateTextField } from 'utils/validateTextField';

import { CustomQuizDetailDefinitionDto } from '@/_generated/api-clients/xp';

export interface BuildQuizzesProps {
  handlePassingGrade?: (grade?: number) => void;
  isEditMode?: boolean;
  questionIndex: number;
  questionTemplateType?: { isMultipleChoiceTemplate: boolean };
  quiz: CustomQuizDetailDefinitionDto;
  setQuiz: React.Dispatch<React.SetStateAction<CustomQuizDetailDefinitionDto>>;
  setValidationMode: (validate: boolean) => void;
  validationMode: boolean;
}

const BuildQuizzesAnswers: React.FC<BuildQuizzesProps> = ({
  questionIndex,
  questionTemplateType,
  quiz,
  setQuiz,
  setValidationMode,
  validationMode,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const userLanguage = auth?.language ?? 'en';

  const handleOptionText = (value: string, optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.map((answer, ai) =>
                ai === optionIndex
                  ? {
                      ...answer,
                      answer: { ...answer.answer, [userLanguage]: value },
                    }
                  : answer,
              ),
            }
          : question,
      ),
    }));
  };

  const handleCorrectOption = (optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.map((answer, ai) => ({
                ...answer,
                correct: ai === optionIndex,
              })),
            }
          : question,
      ),
    }));
  };

  const addOption = () => {
    setValidationMode(false);
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: [...question.answers, Constants.DEFAULT_QUIZ_ANSWER],
            }
          : question,
      ),
    }));
  };

  const deleteOption = (optionIndex: number) => {
    setQuiz(prevState => ({
      ...prevState,
      questions: prevState.questions.map((question, qi) =>
        qi === questionIndex
          ? {
              ...question,
              answers: question.answers.filter((_, ai) => ai !== optionIndex),
            }
          : question,
      ),
    }));
  };

  return (
    <div className="mx-0 my-4">
      <RadioGroup
        defaultValue={''}
        orientation="vertical"
        onChange={value => handleCorrectOption(Number(value))}
        className="w-full!"
      >
        {quiz.questions[questionIndex]?.answers.map((option, optionIndex) => (
          <div
            className={cn(
              'mb-4 flex rounded border border-solid border-transparent pt-2 pr-2 pb-3 pl-1',
              {
                'border-feedback-success-base bg-[rgba(7,126,76,0.1)]':
                  option.correct,
              },
            )}
            key={`questionOption-${optionIndex}`}
          >
            <Radio
              className={cn('flex pr-2', {
                '[&::before]:border-feedback-success-base! [&::before]:border-1.5! [&::before]:border-solid!':
                  option.correct,
              })}
              value={String(optionIndex)}
            />
            <div
              className={cn('relative w-full', {
                '[&_input]:pr-12': option.correct,
              })}
            >
              <TextField
                data-testid={`EnterAnswer${optionIndex + 1}`}
                errorText={
                  !validateTextField(option.answer[userLanguage] ?? '') &&
                  validationMode
                    ? t('TrainingPlans.buildQuizzes.errorQuestion')
                    : ''
                }
                fullWidth
                label={`${t('TrainingPlans.buildQuizzes.option')} ${
                  optionIndex + 1
                }`}
                maxLength={255}
                onChange={e => handleOptionText(e.target.value, optionIndex)}
                placeholder={t('TrainingPlans.buildQuizzes.enterAnswer')}
                required
                value={option.answer[userLanguage] ?? ''}
              />
              {option.correct && (
                <IconCheck
                  size={20}
                  className="text-feedback-success-base! absolute! top-[calc(50%-10px)]! right-4!"
                />
              )}
            </div>
            {(quiz.questions[questionIndex]?.answers.length ?? 0) > 2 && (
              <div className="mx-2 mt-6 mb-0 flex h-12 items-center">
                <IconX
                  onClick={() => deleteOption(optionIndex)}
                  size={32}
                  className="text-grayscale-gray6!"
                />
              </div>
            )}
          </div>
        ))}
      </RadioGroup>
      {questionTemplateType?.isMultipleChoiceTemplate && (
        <Button
          className="text-brand-navy!"
          color="secondary"
          onClick={addOption}
          size="lg"
          variant="text"
        >
          <IconPlus />
          {t('Button.addOption')}
        </Button>
      )}
    </div>
  );
};

export default BuildQuizzesAnswers;
